import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from '@/hooks/use-toast';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('sessionId'));
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Setup axios interceptor to handle 401 errors globally
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && sessionId) {
          console.warn('Session expired or invalid. Logging out...');
          toast({
            title: "Session Expired",
            description: "Your session has expired. Please log in again.",
            variant: "destructive",
          });
          logout();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [sessionId, toast]);

  useEffect(() => {
    if (sessionId) {
      axios.defaults.headers.common['X-Session-Id'] = sessionId;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { session_id, user: userData } = response.data;
    
    localStorage.setItem('sessionId', session_id);
    axios.defaults.headers.common['X-Session-Id'] = session_id;
    setSessionId(session_id);
    setUser(userData);
    
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('sessionId');
      delete axios.defaults.headers.common['X-Session-Id'];
      setSessionId(null);
      setUser(null);
    }
  };

  const isAdmin = user?.role === 'ADMIN';
  const isManager = user?.role === 'MANAGER';
  const isEmployee = user?.role === 'EMPLOYEE';
  const canManageUsers = isAdmin;
  const canManageProjects = isAdmin;
  const canReviewTimesheets = isAdmin || isManager;

  return (
    <AuthContext.Provider value={{
      user,
      sessionId,
      loading,
      login,
      logout,
      isAdmin,
      isManager,
      isEmployee,
      canManageUsers,
      canManageProjects,
      canReviewTimesheets
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
